import { useState } from 'react';
import { Plus } from 'lucide-react';

import { ComboField, Switch } from '~/components/form';
import { Alert, Button, DataTable, DropdownMenu, type DataTableColumn } from '~/components/ui';
import { schemaFieldsToJsonSchema } from '~/lib/ai/jsonSchema';
import {
  createDefaultField,
  duplicateField,
  moveField,
  removeField,
  updateField,
} from '~/lib/ai/schema';
import type { SchemaField } from '~/lib/ai/schema';
import type { FieldCatalogEntry } from '~/modules/query/ai/types';

export interface SchemaBuilderProps {
  fields: SchemaField[];
  /** Used for the generated schema's title. */
  assistantName: string;
  catalog: FieldCatalogEntry[];
  catalogLoading: boolean;
  onChange: (fields: SchemaField[]) => void;
}

interface FieldRow {
  field: SchemaField;
  index: number;
}

/**
 * The assistant's input schema, built from the business field catalog. Rows are
 * add-row drafts: the property name is picked, never typed, so the stored schema
 * cannot name a field the runtime does not know.
 */
export default function SchemaBuilder({
  fields,
  assistantName,
  catalog,
  catalogLoading,
  onChange,
}: SchemaBuilderProps) {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { invalidPropertyNames } = schemaFieldsToJsonSchema(fields, catalog, assistantName);
  const usedKeys = new Set(fields.map((field) => field.propertyName).filter(Boolean));

  const selectField = (id: string, entry: FieldCatalogEntry) => {
    onChange(
      updateField(fields, id, (field) => ({
        ...field,
        propertyName: entry.key,
        type: entry.type,
      })),
    );
  };

  const duplicate = (id: string) => {
    const previousIds = new Set(fields.map((field) => field.id));
    const duplicated = duplicateField(fields, id);
    const copy = duplicated.find((field) => !previousIds.has(field.id));

    // A copy must not carry the source's catalog key: two rows cannot bind the
    // same property, and the copy is blank by definition.
    onChange(
      copy
        ? updateField(duplicated, copy.id, (field) => ({
            ...field,
            propertyName: '',
            type: 'string',
          }))
        : duplicated,
    );
  };

  const columns: DataTableColumn<FieldRow>[] = [
    {
      id: 'number',
      header: '#',
      className: 'w-16',
      cell: (row) => <span className="text-gray-500 dark:text-gray-400">{row.index + 1}</span>,
    },
    {
      id: 'field',
      header: 'Business field',
      className: 'min-w-64',
      cell: (row) => (
        <ComboField
          id={`schema-field-${row.field.id}`}
          testId={`ai-digital-assistants-schema-field-${row.index + 1}`}
          ariaLabel={`Business field for row ${row.index + 1}`}
          placeholder="Select a business field"
          value={row.field.propertyName}
          options={catalog
            .filter((entry) => entry.key === row.field.propertyName || !usedKeys.has(entry.key))
            .map((entry) => ({ value: entry.key, label: entry.label }))}
          searchable
          onChange={(value) => {
            const entry = catalog.find((candidate) => candidate.key === value);
            if (entry) {
              selectField(row.field.id, entry);
            }
          }}
        />
      ),
    },
    {
      id: 'required',
      header: 'Required',
      className: 'w-24',
      cell: (row) => (
        <Switch
          ariaLabel={`Required: ${row.field.propertyName || `row ${row.index + 1}`}`}
          checked={row.field.required}
          onChange={(required) =>
            onChange(updateField(fields, row.field.id, (field) => ({ ...field, required })))
          }
          testId={`ai-digital-assistants-schema-required-${row.index + 1}`}
        />
      ),
    },
    {
      id: 'description',
      header: 'Description',
      className: 'w-full',
      cell: (row) => (
        <input
          type="text"
          aria-label={`Description for row ${row.index + 1}`}
          className="field-control"
          placeholder="Defaults to the catalog description"
          value={row.field.description}
          onChange={(event) =>
            onChange(
              updateField(fields, row.field.id, (field) => ({
                ...field,
                description: event.target.value,
              })),
            )
          }
          data-testid={`ai-digital-assistants-schema-description-${row.index + 1}`}
        />
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => (
        <div className="flex w-full items-center justify-center">
          <DropdownMenu
            testId={`ai-digital-assistants-schema-actions-${row.index + 1}`}
            isOpen={openMenuId === row.field.id}
            onOpenChange={(open) => setOpenMenuId(open ? row.field.id : null)}
            items={[
              { label: 'Duplicate', testId: 'duplicate', onClick: () => duplicate(row.field.id) },
              {
                label: 'Move up',
                testId: 'move-up',
                disabled: row.index === 0,
                onClick: () => onChange(moveField(fields, row.field.id, 'up')),
              },
              {
                label: 'Move down',
                testId: 'move-down',
                disabled: row.index === fields.length - 1,
                onClick: () => onChange(moveField(fields, row.field.id, 'down')),
              },
              { divider: true },
              {
                label: 'Remove',
                testId: 'remove',
                destructive: true,
                onClick: () => onChange(removeField(fields, row.field.id)),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const rows: FieldRow[] = fields.map((field, index) => ({ field, index }));

  return (
    <div>
      {invalidPropertyNames.length > 0 ? (
        <Alert variant="error">
          <p className="text-sm text-gray-800 dark:text-white/90">
            These fields are no longer in the business field catalog:{' '}
            {invalidPropertyNames.join(', ')}. Choose a different field for each affected row.
            Saving is blocked until you do.
          </p>
        </Alert>
      ) : null}

      <div className={invalidPropertyNames.length > 0 ? 'mt-4' : undefined}>
        <DataTable<FieldRow>
          testId="ai-digital-assistants-schema-table"
          columns={columns}
          data={rows}
          rowKey={(row) => row.field.id}
          loading={catalogLoading}
          emptyMessage="No input fields yet"
          emptyDescription="Add a field to describe the structured input this assistant accepts. Leave the table empty for an assistant that takes none."
        />
      </div>

      <div className="mt-3">
        <Button
          variant="outline"
          startIcon={<Plus className="size-4" aria-hidden="true" />}
          onClick={() => onChange([...fields, createDefaultField()])}
          disabled={catalogLoading}
          testId="ai-digital-assistants-schema-add"
        >
          Add field
        </Button>
      </div>
    </div>
  );
}
